// Browser-compatible filesystem implementation
import { globalVFS } from './storage/VFS';
import { supabase } from '../integrations/supabase/client';

export interface FileSystemItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  path: string;
  size?: string;
  modified: string;
  modifiedMs?: number; // NEW: raw timestamp for sorting
  content?: string; // For text files
  extension?: string;
  originalPath?: string;
  children?: FileSystemItem[];
}

// Environment detection
const isElectron = () => {
  return typeof window !== 'undefined' && window.navigator?.userAgent?.includes('Electron');
};

// Host filesystem access (for Electron)
const getHostFS = () => {
  if (isElectron() && typeof window !== 'undefined' && (window as any).electronAPI) {
    return (window as any).electronAPI.fs;
  }
  return null;
};

export const createFileSystem = (): FileSystemItem => ({
  id: 'root',
  name: 'C:',
  type: 'folder',
  path: 'C:\\',
  modified: '12/08/2024',
  children: [
    {
      id: 'user',
      name: 'User',
      type: 'folder',
      path: 'C:\\User',
      modified: '12/08/2024',
      children: [
        {
          id: 'default',
          name: 'Default',
          type: 'folder',
          path: 'C:\\User\\Default',
          modified: '12/08/2024',
          children: [
            {
              id: 'desktop',
              name: 'Desktop',
              type: 'folder',
              path: 'C:\\User\\Default\\Desktop',
              modified: '12/08/2024',
              children: []
            },
            {
              id: 'documents',
              name: 'Documents',
              type: 'folder',
              path: 'C:\\User\\Default\\Documents',
              modified: '12/08/2024',
              children: [
                {
                  id: 'letter',
                  name: 'Letter.txt',
                  type: 'file',
                  path: 'C:\\User\\Default\\Documents\\Letter.txt',
                  size: '2 KB',
                  modified: '12/08/2024',
                  extension: 'txt',
                  content: 'Dear Friend,\n\nI hope this letter finds you well. This is a sample text file that you can edit in Notepad.\n\nBest regards,\nDefault'
                },
                {
                  id: 'notes',
                  name: 'Notes.txt',
                  type: 'file',
                  path: 'C:\\User\\Default\\Documents\\Notes.txt',
                  size: '1 KB',
                  modified: '12/08/2024',
                  extension: 'txt',
                  content: 'Personal Notes:\n\n- Buy groceries\n- Call mom\n- Finish project\n- Read a book'
                },
                {
                  id: 'work',
                  name: 'Work',
                  type: 'folder',
                  path: 'C:\\User\\Default\\Documents\\Work',
                  modified: '12/08/2024',
                  children: [
                    {
                      id: 'report',
                      name: 'Report.txt',
                      type: 'file',
                      path: 'C:\\User\\Default\\Documents\\Work\\Report.txt',
                      size: '3 KB',
                      modified: '12/08/2024',
                      extension: 'txt',
                      content: 'Monthly Report\n\nQ4 Performance:\n- Sales increased by 15%\n- Customer satisfaction: 98%\n- New products launched: 3\n\nNext quarter goals:\n- Expand market reach\n- Improve efficiency\n- Launch mobile app'
                    }
                  ]
                }
              ]
            },
            {
              id: 'music',
              name: 'Music',
              type: 'folder',
              path: 'C:\\User\\Default\\Music',
              modified: '12/08/2024',
              children: [
                {
                  id: 'chill-vibes',
                  name: 'Chill Vibes.wav',
                  type: 'file',
                  path: 'C:\\User\\Default\\Music\\Chill Vibes.wav',
                  size: '4.2 MB',
                  modified: '12/08/2024',
                  extension: 'wav',
                  content: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.wav'
                },
                {
                  id: 'electronic-beat',
                  name: 'Electronic Beat.wav',
                  type: 'file',
                  path: 'C:\\User\\Default\\Music\\Electronic Beat.wav',
                  size: '3.8 MB',
                  modified: '12/08/2024',
                  extension: 'wav',
                  content: 'https://www.soundjay.com/misc/sounds/fail-buzzer-02.wav'
                },
                {
                  id: 'ambient-space',
                  name: 'Ambient Space.wav',
                  type: 'file',
                  path: 'C:\\User\\Default\\Music\\Ambient Space.wav',
                  size: '5.1 MB',
                  modified: '12/08/2024',
                  extension: 'wav',
                  content: 'https://www.soundjay.com/misc/sounds/magic-chime-02.wav'
                },
                {
                  id: 'retro-synth',
                  name: 'Retro Synth.wav',
                  type: 'file',
                  path: 'C:\\User\\Default\\Music\\Retro Synth.wav',
                  size: '3.5 MB',
                  modified: '12/08/2024',
                  extension: 'wav',
                  content: 'https://www.soundjay.com/misc/sounds/spring-boing-01.wav'
                },
                {
                  id: 'nature-sounds',
                  name: 'Nature Sounds.wav',
                  type: 'file',
                  path: 'C:\\User\\Default\\Music\\Nature Sounds.wav',
                  size: '6.2 MB',
                  modified: '12/08/2024',
                  extension: 'wav',
                  content: 'https://www.soundjay.com/misc/sounds/water-drop-01.wav'
                }
              ]
            },
            {
              id: 'videos',
              name: 'Videos',
              type: 'folder',
              path: 'C:\\User\\Default\\Videos',
              modified: '12/08/2024',
              children: []
            },
            {
              id: 'pictures',
              name: 'Pictures',
              type: 'folder',
              path: 'C:\\User\\Default\\Pictures',
              modified: '12/08/2024',
              children: [
                {
                  id: 'vacation',
                  name: 'Vacation',
                  type: 'file',
                  path: 'C:\\User\\Default\\Pictures\\Vacation',
                  modified: '12/08/2024',
                  children: [
                    {
                      id: 'mountain-landscape',
                      name: 'Mountain Landscape.jpg',
                      type: 'file',
                      path: 'C:\\User\\Default\\Pictures\\Vacation\\Mountain Landscape.jpg',
                      size: '2.4 MB',
                      modified: '12/08/2024',
                      extension: 'jpg',
                      content: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=600&fit=crop'
                    },
                    {
                      id: 'ocean-waves',
                      name: 'Ocean Waves.jpg',
                      type: 'file',
                      path: 'C:\\User\\Default\\Pictures\\Vacation\\Ocean Waves.jpg',
                      size: '3.1 MB',
                      modified: '12/08/2024',
                      extension: 'jpg',
                      content: 'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?w=800&h=600&fit=crop'
                    },
                    {
                      id: 'forest-path',
                      name: 'Forest Path.jpg',
                      type: 'file',
                      path: 'C:\\User\\Default\\Pictures\\Vacation\\Forest Path.jpg',
                      size: '1.8 MB',
                      modified: '12/08/2024',
                      extension: 'jpg',
                      content: 'https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=800&h=600&fit=crop'
                    },
                    {
                      id: 'starry-night',
                      name: 'Starry Night.jpg',
                      type: 'file',
                      path: 'C:\\User\\Default\\Pictures\\Vacation\\Starry Night.jpg',
                      size: '2.7 MB',
                      modified: '12/08/2024',
                      extension: 'jpg',
                      content: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=800&h=600&fit=crop'
                    },
                    {
                      id: 'desert-dunes',
                      name: 'Desert Dunes.jpg',
                      type: 'file',
                      path: 'C:\\User\\Default\\Pictures\\Vacation\\Desert Dunes.jpg',
                      size: '2.2 MB',
                      modified: '12/08/2024',
                      extension: 'jpg',
                      content: 'https://images.unsplash.com/photo-1482881497185-d4a9ddbe4151?w=800&h=600&fit=crop'
                    },
                    {
                      id: 'alpine-lake',
                      name: 'Alpine Lake.jpg',
                      type: 'file',
                      path: 'C:\\User\\Default\\Pictures\\Vacation\\Alpine Lake.jpg',
                      size: '3.5 MB',
                      modified: '12/08/2024',
                      extension: 'jpg',
                      content: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=800&h=600&fit=crop'
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'program-files',
      name: 'Program Files',
      type: 'folder',
      path: 'C:\\Program Files',
      modified: '12/08/2024',
      children: [
        {
          id: 'common-files',
          name: 'Common Files',
          type: 'folder',
          path: 'C:\\Program Files\\Common Files',
          modified: '12/08/2024',
          children: []
        },
        {
          id: 'modules',
          name: 'Modules',
          type: 'folder',
          path: 'C:\\Program Files\\Modules',
          modified: '12/08/2024',
          children: []
        }
      ]
    },
    {
      id: 'MapleOS',
      name: 'MapleOS',
      type: 'folder',
      path: 'C:\\MapleOS',
      modified: '12/08/2024',
      children: [
        {
          id: 'system32',
          name: 'System32',
          type: 'folder',
          path: 'C:\\MapleOS\\System32',
          modified: '12/08/2024',
          children: []
        },
        {
          id: 'temp',
          name: 'Temp',
          type: 'folder',
          path: 'C:\\MapleOS\\Temp',
          modified: '12/08/2024',
          children: []
        }
      ]
    },
    {
      id: 'readme',
      name: 'readme.txt',
      type: 'file',
      path: 'C:\\readme.txt',
      size: '2 KB',
      modified: '12/08/2024',
      extension: 'txt',
      content: 'Welcome to MapleOS 26.2!\n\nThis is your C: drive. You can explore folders and open text files.\n\nDouble-click on any .txt file to open it in Notepad.\n\nEnjoy exploring your virtual file system!'
    },
    {
      id: 'autoexec',
      name: 'autoexec.bat',
      type: 'file',
      path: 'C:\\autoexec.bat',
      size: '512 bytes',
      modified: '12/08/2024',
      extension: 'bat',
      content: '@echo off\nREM This is a sample batch file\nSET PATH=C:\\MapleOS;C:\\MapleOS\\COMMAND\necho System initialized'
    },
    {
      id: 'config',
      name: 'config.sys',
      type: 'file',
      path: 'C:\\config.sys',
      size: '1 KB',
      modified: '12/08/2024',
      extension: 'sys',
      content: 'DEVICE=C:\\MapleOS\\HIMEM.SYS\nDEVICE=C:\\MapleOS\\EMM386.EXE\nFILES=30\nBUFFERS=20'
    },
    {
      id: 'games',
      name: 'Games',
      type: 'folder',
      path: 'C:\\Games',
      modified: '12/08/2024',
      children: [
        {
          id: 'mekwarrior',
          name: 'MekWarrior',
          type: 'folder',
          path: 'C:\\Games\\MekWarrior',
          modified: '12/08/2024',
          children: [
            {
              id: 'mechs',
              name: 'Mechs',
              type: 'folder',
              path: 'C:\\Games\\MekWarrior\\Mechs',
              modified: '12/08/2024',
              children: [
                {
                  id: 'annihilator',
                  name: 'Annihilator',
                  type: 'folder',
                  path: 'C:\\Games\\MekWarrior\\Mechs\\Annihilator',
                  modified: '12/08/2024',
                  children: []
                },
                {
                  id: 'locust',
                  name: 'Locust',
                  type: 'folder',
                  path: 'C:\\Games\\MekWarrior\\Mechs\\Locust',
                  modified: '12/08/2024',
                  children: []
                },
                {
                  id: 'jenner',
                  name: 'Jenner',
                  type: 'folder',
                  path: 'C:\\Games\\MekWarrior\\Mechs\\Jenner',
                  modified: '12/08/2024',
                  children: []
                },
                {
                  id: 'raven',
                  name: 'Raven',
                  type: 'folder',
                  path: 'C:\\Games\\MekWarrior\\Mechs\\Raven',
                  modified: '12/08/2024',
                  children: []
                }
              ]
            },
            {
              id: 'config',
              name: 'Config',
              type: 'folder',
              path: 'C:\\Games\\MekWarrior\\Config',
              modified: '12/08/2024',
              children: []
            },
            {
              id: 'data',
              name: 'Data',
              type: 'folder',
              path: 'C:\\Games\\MekWarrior\\Data',
              modified: '12/08/2024',
              children: []
            },
            {
              id: 'save',
              name: 'Save',
              type: 'folder',
              path: 'C:\\Games\\MekWarrior\\Save',
              modified: '12/08/2024',
              children: []
            },
            {
              id: 'missions',
              name: 'Missions',
              type: 'folder',
              path: 'C:\\Games\\MekWarrior\\Missions',
              modified: '12/08/2024',
              children: []
            },
            {
              id: 'readme',
              name: 'README.TXT',
              type: 'file',
              path: 'C:\\Games\\MekWarrior\\README.TXT',
              modified: '12/08/2024',
              extension: '.txt',
              size: '2.1 KB'
            }
          ]
        },
        {
          id: 'dos',
          name: 'DOS',
          type: 'folder',
          path: 'C:\\Games\\DOS',
          modified: '12/08/2024',
          children: [
            {
              id: 'digger-jsdos',
              name: 'Digger.jsdos',
              type: 'file',
              path: 'C:\\Games\\DOS\\Digger.jsdos',
              size: '2.5 MB',
              modified: '12/08/2024',
              extension: 'jsdos',
              content: 'https://v8.js-dos.com/bundles/digger.jsdos'
            },
            {
              id: 'prince-jsdos',
              name: 'Prince.jsdos',
              type: 'file',
              path: 'C:\\Games\\DOS\\Prince.jsdos',
              size: '8.2 MB',
              modified: '12/08/2024',
              extension: 'jsdos',
              content: 'https://v8.js-dos.com/bundles/prince.jsdos'
            },
            {
              id: 'doom-jsdos',
              name: 'Doom.jsdos',
              type: 'file',
              path: 'C:\\Games\\DOS\\Doom.jsdos',
              size: '12.4 MB',
              modified: '12/08/2024',
              extension: 'jsdos',
              content: 'https://v8.js-dos.com/bundles/doom.jsdos'
            }
          ]
        }
      ]
    },
    {
      id: 'recycle-bin',
      name: '$Recycle.Bin',
      type: 'folder',
      path: 'C:\\$Recycle.Bin',
      modified: '12/08/2024',
      children: []
    }
  ]
});

export class FileSystemNavigator {
  private fileSystem: FileSystemItem;
  private history: string[] = [];
  private historyIndex: number = -1;
  private virtualFS: Map<string, { type: 'file' | 'folder', content?: string, modified: Date, originalPath?: string }> = new Map();
  private hostFS: any;
  private isInitialized: boolean = false;
  private static isGlobalInitialized: boolean = false; // Static flag to prevent multiple VFS initializations

  constructor() {
    this.fileSystem = createFileSystem();
    this.hostFS = getHostFS();
    
    // Only initialize virtual filesystem once globally to prevent VFS spam
    if (!FileSystemNavigator.isGlobalInitialized) {
      FileSystemNavigator.isGlobalInitialized = true;
      this.initializeVirtualFS();
    } else {
      // For subsequent instances, just mark as initialized without VFS sync
      this.isInitialized = true;
      this.populateVirtualFS(this.fileSystem);
    }
  }

  private normalizePath(path: string): string {
    const normalized = path
      .replace(/\//g, '\\')
      .replace(/\\+/g, '\\');

    if (/^[A-Z]:$/i.test(normalized)) {
      return `${normalized}\\`;
    }

    if (/^[A-Z]:\\/i.test(normalized)) {
      return normalized;
    }

    return `C:\\${normalized.replace(/^\\+/, '')}`;
  }

  private async initializeVirtualFS() {
    try {
      // Check if we already have data in persistent storage
      const existingFiles = await globalVFS.getAllFiles();
      console.log('Found', existingFiles.length, 'existing files in persistent storage');

      if (existingFiles.length === 0) {
        // No existing data, initialize with default structure
        console.log('No existing data found, creating new default filesystem structure');

        // Get an initial timestamp from Supabase for first-boot items
        const initialTimestamp = await this.getInitialBootTimestamp();
        this.populateVirtualFS(this.fileSystem, initialTimestamp);
        await this.saveAllToStorage(initialTimestamp);
      } else {
        // Load existing data from persistent storage
        console.log('Loading existing filesystem data from persistent storage');
        await this.syncWithVFS();
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize VFS:', error);
      // Fallback to in-memory only with default structure
      this.populateVirtualFS(this.fileSystem);
      this.isInitialized = true;
    }
  }

  // Sync the navigator's virtualFS with the global VFS
  async syncWithVFS() {
    try {
      const existingFiles = await globalVFS.getAllFiles();
      console.log('Syncing with VFS, found', existingFiles.length, 'files');

      // Clear current virtualFS and rebuild from VFS
      this.virtualFS.clear();

      // First populate with static structure (keep original modified strings for display when not first-boot)
      this.populateVirtualFS(this.fileSystem);

      // Then add all files from VFS
      for (const fileData of existingFiles) {
        const normalizedPath = this.normalizePath(fileData.path);
        this.virtualFS.set(normalizedPath, {
          type: fileData.type,
          content: fileData.content,
          modified: fileData.modified
        });
        console.log('Added from VFS:', normalizedPath);
      }

      console.log('Sync complete, virtualFS now has', this.virtualFS.size, 'entries');
    } catch (error) {
      console.error('Failed to sync with VFS:', error);
    }
  }

  private populateVirtualFS(item: FileSystemItem, overrideModified?: Date) {
    this.virtualFS.set(this.normalizePath(item.path), {
      type: item.type,
      content: item.content,
      modified: overrideModified ?? new Date(item.modified)
    });

    if (item.children) {
      item.children.forEach(child => this.populateVirtualFS(child, overrideModified));
    }
  }

  private async saveAllToStorage(overrideModified?: Date) {
    try {
      for (const [path, item] of this.virtualFS) {
        if (item.type === 'file') {
          await globalVFS.save(path, item.content ?? '', overrideModified ?? item.modified);
        } else if (item.type === 'folder') {
          await globalVFS.createDirectory(path, overrideModified ?? item.modified);
        }
      }
      console.log('Saved all files to persistent storage');
    } catch (error) {
      console.error('Failed to save to persistent storage:', error);
    }
  }

  // Fetch a Supabase-based timestamp to use for first-boot seeded items
  private async getInitialBootTimestamp(): Promise<Date> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        const { data, error } = await supabase
          .from('profiles')
          .select('created_at, updated_at')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          const profileRow = data as { created_at?: string | null; updated_at?: string | null };
          const iso = profileRow.created_at ?? profileRow.updated_at;
          if (iso) {
            return new Date(iso);
          }
        }

      }
    } catch (e) {
      // fall through to local time
    }
    // Fallback: local time if Supabase not available or user not logged in
    return new Date();
  }

  async waitForInitialization(): Promise<void> {
    return new Promise((resolve) => {
      if (this.isInitialized) {
        resolve();
        return;
      }
      
      const checkInit = () => {
        if (this.isInitialized) {
          resolve();
        } else {
          setTimeout(checkInit, 10);
        }
      };
      checkInit();
    });
  }

  async resetFileSystem(): Promise<void> {
    try {
      // Clear all persistent storage
      await globalVFS.clearAll();
      
      // Clear virtual filesystem
      this.virtualFS.clear();
      
      // Reset initialization flag
      this.isInitialized = false;
      
      // Reinitialize with new default structure
      await this.initializeVirtualFS();
      
      console.log('Filesystem reset successfully with new structure');
    } catch (error) {
      console.error('Failed to reset filesystem:', error);
    }
  }


  navigate(path: string): void {
    if (this.historyIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.historyIndex + 1);
    }
    this.history.push(path);
    this.historyIndex++;
  }

  canGoBack(): boolean {
    return this.historyIndex > 0;
  }

  canGoForward(): boolean {
    return this.historyIndex < this.history.length - 1;
  }

  goBack(): string | null {
    if (this.canGoBack()) {
      this.historyIndex--;
      return this.history[this.historyIndex];
    }
    return null;
  }

  goForward(): string | null {
    if (this.canGoForward()) {
      this.historyIndex++;
      return this.history[this.historyIndex];
    }
    return null;
  }

  getCurrentPath(): string {
    return this.history[this.historyIndex] || 'C:\\';
  }

  findItemByPath(path: string): FileSystemItem | null {
    const fsItem = this.virtualFS.get(path);
    if (!fsItem) return null;
    
    const name = path === 'C:\\' ? 'C:' : path.split('\\').pop() || '';
    let extension: string | undefined;
    
    if (fsItem.type === 'file') {
      extension = name.split('.').pop()?.toLowerCase();
    }
    
    return {
      id: path.replace(/[\\:]/g, '_'),
      name,
      type: fsItem.type,
      path,
      size: fsItem.type === 'file' && fsItem.content ? this.formatFileSize(new TextEncoder().encode(fsItem.content).length) : undefined,
      modified: fsItem.modified.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
      modifiedMs: fsItem.modified.getTime(), // NEW: raw timestamp
      content: fsItem.content,
      extension,
      originalPath: fsItem.originalPath,
      children: fsItem.type === 'file' ? undefined : this.getChildren(path)
    };
  }

  private getChildren(path: string): FileSystemItem[] {
    console.log('NEW getChildren called for path:', path);
    const children: FileSystemItem[] = [];
    
    // Debug: Show all entries that start with this path
    console.log('Looking for children of:', path);
    const matchingEntries = [];
    for (const [fsPath, fsItem] of this.virtualFS) {
      if (fsPath.startsWith(path) && fsPath !== path) {
        matchingEntries.push({ path: fsPath, type: fsItem.type });
      }
    }
    console.log('All entries that start with', path + ':', matchingEntries);
    
    for (const [fsPath, _] of this.virtualFS) {
      if (fsPath.startsWith(path) && fsPath !== path) {
        const relativePath = fsPath.substring(path.length);
        const trimmedPath = relativePath.startsWith('\\') ? relativePath.substring(1) : relativePath;
        console.log(`  Processing ${fsPath}: relativePath="${relativePath}", trimmedPath="${trimmedPath}"`);
        
        // Only direct children (no nested paths)
        if (trimmedPath && !trimmedPath.includes('\\')) {
          console.log(`    Found direct child: ${fsPath}`);
          const childItem = this.findItemByPath(fsPath);
          if (childItem) {
            console.log(`    Adding child: ${childItem.name} (${childItem.type})`);
            children.push(childItem);
          } else {
            console.log(`    Failed to create childItem for: ${fsPath}`);
          }
        } else {
          console.log(`    Skipping (not direct child): trimmedPath="${trimmedPath}", includes\\=${trimmedPath.includes('\\')}`);
        }
      }
    }
    
    console.log(`getChildren returning ${children.length} children for ${path}:`, children.map(c => c.name));
    return children;
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  pathExists(path: string): boolean {
    return this.findItemByPath(path) !== null;
  }

  getAllPaths(): string[] {
    const paths: string[] = [];
    
    const collectPaths = (item: FileSystemItem) => {
      paths.push(item.path);
      if (item.children) {
        item.children.forEach(collectPaths);
      }
    };
    
    collectPaths(this.fileSystem);
    return paths;
  }

  getItemsInPath(path: string): FileSystemItem[] {
    console.log('getItemsInPath called for:', path);
    
    // Debug: Show all virtual FS entries
    console.log('Current virtualFS entries:');
    for (const [fsPath, fsItem] of this.virtualFS) {
      console.log(`  ${fsPath}: ${fsItem.type}`);
    }
    
    const item = this.findItemByPath(path);
    console.log('findItemByPath result for', path, ':', item ? `${item.name} with ${item.children?.length || 0} children` : 'null');
    
    return item?.children || [];
  }

  getParentPath(path: string): string {
    const normalizedPath = this.normalizePath(path);
    if (normalizedPath === 'C:\\') return 'C:\\';
    const parts = normalizedPath.split('\\').filter(part => part && part !== 'C:');
    parts.pop();
    return parts.length === 0 ? 'C:\\' : `C:\\${parts.join('\\')}`;
  }

  async updateFileContent(path: string, content: string): Promise<boolean> {
    try {
      const normalized = this.normalizePath(path);
      this.virtualFS.set(normalized, {
        type: 'file',
        content,
        modified: new Date()
      });
      
      // Save to persistent storage
      await globalVFS.save(normalized, content);
      console.log('File updated in virtual FS and persistent storage:', normalized);
      return true;
    } catch (e) {
      console.error('Failed to update file:', e);
      return false;
    }
  }

  async createFile(path: string, content: string = ''): Promise<boolean> {
    try {
      const normalized = this.normalizePath(path);
      // Ensure parent directory exists
      const parentPath = this.getParentPath(normalized);
      if (parentPath !== normalized && !this.virtualFS.has(parentPath)) {
        await this.createFolder(parentPath);
      }
      
      this.virtualFS.set(normalized, {
        type: 'file',
        content,
        modified: new Date()
      });
      
      // Save to persistent storage
      await globalVFS.save(normalized, content);
      console.log('File created in virtual FS and persistent storage:', normalized);
      return true;
    } catch (e) {
      console.error('Failed to create file:', e);
      return false;
    }
  }

  async createFolder(path: string): Promise<boolean> {
    console.log('createFolder called with path:', path);
    try {
      const normalized = this.normalizePath(path);
      // Ensure parent directory exists
      const parentPath = this.getParentPath(normalized);
      console.log('Parent path:', parentPath);
      if (parentPath !== normalized && !this.virtualFS.has(parentPath)) {
        console.log('Parent does not exist, creating:', parentPath);
        await this.createFolder(parentPath);
      }
      
      console.log('Adding to virtualFS:', normalized);
      this.virtualFS.set(normalized, {
        type: 'folder',
        modified: new Date()
      });
      
      console.log('virtualFS now has', this.virtualFS.size, 'entries');
      console.log('virtualFS contains', normalized, ':', this.virtualFS.has(normalized));
      
      // Save to persistent storage
      await globalVFS.createDirectory(normalized);
      console.log('Folder created in virtual FS and persistent storage:', normalized);
      return true;
    } catch (e) {
      console.error('Failed to create folder:', e);
      return false;
    }
  }

  // Create user-specific directories based on username
  async createUserDirectories(username: string): Promise<boolean> {
    try {
      const sanitizedUsername = username.replace(/[^a-zA-Z0-9]/g, '');
      const userBasePath = `C:\\User\\${sanitizedUsername}`;
      
      // Check if this directory already exists
      const userDir = this.findItemByPath(userBasePath);
      if (userDir) {
        console.log('User directories already exist for:', sanitizedUsername);
        return true;
      }
      
      console.log('Creating user directories for:', sanitizedUsername);
      
      // Create user base directory
      await this.createFolder(userBasePath);
      
      // Create Desktop (empty)
      await this.createFolder(`${userBasePath}\\Desktop`);
      await this.createFolder(`${userBasePath}\\Saved`);
      
      // Create Documents with default files
      await this.createFolder(`${userBasePath}\\Documents`);

      await this.createFile(`${userBasePath}\\Documents\\Letter.txt`, 
        `Dear Friend,\n\nI hope this letter finds you well. This is a sample text file that you can edit in Notepad.\n\nBest regards,\n${sanitizedUsername}`);
      await this.createFile(`${userBasePath}\\Documents\\Notes.txt`, 
        'Personal Notes:\n\n- Buy groceries\n- Call mom\n- Finish project\n- Read a book');
      
      // Create Work subfolder in Documents
      await this.createFolder(`${userBasePath}\\Documents\\Work`);
      await this.createFile(`${userBasePath}\\Documents\\Work\\Report.txt`, 
        'Monthly Report\n\nQ4 Performance:\n- Sales increased by 15%\n- Customer satisfaction: 98%\n- New products launched: 3\n\nNext quarter goals:\n- Expand market reach\n- Improve efficiency\n- Launch mobile app');
      
      // Create Music with default files
      await this.createFolder(`${userBasePath}\\Music`);
      await this.createFile(`${userBasePath}\\Music\\Chill Vibes.wav`, 
        'https://www.soundjay.com/misc/sounds/bell-ringing-05.wav');
      await this.createFile(`${userBasePath}\\Music\\Electronic Beat.wav`, 
        'https://www.soundjay.com/misc/sounds/fail-buzzer-02.wav');
      await this.createFile(`${userBasePath}\\Music\\Ambient Space.wav`, 
        'https://www.soundjay.com/misc/sounds/magic-chime-02.wav');
      await this.createFile(`${userBasePath}\\Music\\Retro Synth.wav`, 
        'https://www.soundjay.com/misc/sounds/spring-boing-01.wav');
      await this.createFile(`${userBasePath}\\Music\\Nature Sounds.wav`, 
        'https://www.soundjay.com/misc/sounds/water-drop-01.wav');
      
      // Create Videos (empty)
      await this.createFolder(`${userBasePath}\\Videos`);
      
      // Create Pictures with vacation subfolder and default images
      await this.createFolder(`${userBasePath}\\Pictures`);
      await this.createFolder(`${userBasePath}\\Pictures\\Vacation`);
      await this.createFile(`${userBasePath}\\Pictures\\Vacation\\Mountain Landscape.jpg`, 
        'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=600&fit=crop');
      await this.createFile(`${userBasePath}\\Pictures\\Vacation\\Ocean Waves.jpg`, 
        'https://images.unsplash.com/photo-1500375592092-40eb2168fd21?w=800&h=600&fit=crop');
      await this.createFile(`${userBasePath}\\Pictures\\Vacation\\Forest Path.jpg`, 
        'https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?w=800&h=600&fit=crop');
      await this.createFile(`${userBasePath}\\Pictures\\Vacation\\Starry Night.jpg`, 
        'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=800&h=600&fit=crop');
      await this.createFile(`${userBasePath}\\Pictures\\Vacation\\Desert Dunes.jpg`, 
        'https://images.unsplash.com/photo-1482881497185-d4a9ddbe4151?w=800&h=600&fit=crop');
      await this.createFile(`${userBasePath}\\Pictures\\Vacation\\Alpine Lake.jpg`, 
        'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=800&h=600&fit=crop');
      
      console.log('Successfully created user directories with default files for:', sanitizedUsername);
      
      // Clean up any old user directories that don't match the current username
      await this.cleanupOldUserDirectories(sanitizedUsername);
      
      return true;
    } catch (e) {
      console.error('Failed to create user directories:', e);
      return false;
    }
  }

  // Clean up old user directories that don't match current username
  async cleanupOldUserDirectories(currentUsername: string): Promise<void> {
    try {
      const userFolder = this.findItemByPath('C:\\User');
      if (userFolder && userFolder.children) {
        for (const child of userFolder.children) {
          // Skip Default folder and current user folder
          if (child.name !== 'Default' && child.name !== currentUsername) {
            console.log('Removing old user directory:', child.name);
            await this.deleteItem(child.path);
          }
        }
      }
    } catch (e) {
      console.error('Failed to cleanup old user directories:', e);
    }
  }

  // Host filesystem integration (Electron only)
  async importFromHost(hostPath: string, virtualPath: string): Promise<boolean> {
    if (!this.hostFS) {
      console.warn('Host filesystem access not available (not running in Electron)');
      return false;
    }

    try {
      // Implementation would depend on Electron's fs API
      // This is a placeholder for future Electron integration
      console.log('Would import from host:', hostPath, 'to virtual:', virtualPath);
      return true;
    } catch (e) {
      console.error('Failed to import from host filesystem:', e);
      return false;
    }
  }

  async exportToHost(virtualPath: string, hostPath: string): Promise<boolean> {
    if (!this.hostFS) {
      console.warn('Host filesystem access not available (not running in Electron)');
      return false;
    }

    try {
      // Implementation would depend on Electron's fs API
      // This is a placeholder for future Electron integration
      console.log('Would export virtual:', virtualPath, 'to host:', hostPath);
      return true;
    } catch (e) {
      console.error('Failed to export to host filesystem:', e);
      return false;
    }
  }

  // Get disk usage information
  getDiskUsage(): { used: number; total: number; free: number } {
    let totalBytes = 0;
    
    // Calculate total size of all files
    for (const [path, item] of this.virtualFS) {
      if (item.type === 'file' && item.content) {
        totalBytes += new TextEncoder().encode(item.content).length;
      }
    }
    
    // Simulate a 10GB disk with current usage
    const totalDiskSize = 10 * 1024 * 1024 * 1024; // 10GB
    const usedSpace = totalBytes;
    const freeSpace = totalDiskSize - usedSpace;
    
    return {
      used: usedSpace,
      total: totalDiskSize,
      free: freeSpace
    };
  }

  async moveToRecycleBin(path: string): Promise<boolean> {
    try {
      // Get the item before moving it
      const item = this.findItemByPath(path);
      if (!item) {
        console.error('Item not found:', path);
        return false;
      }

      const parentPath = this.getParentPath(path);

      // Create recycle bin path if it doesn't exist
      const recycleBinPath = 'C:\\$Recycle.Bin';
      if (!this.virtualFS.has(recycleBinPath)) {
        this.virtualFS.set(recycleBinPath, {
          type: 'folder',
          modified: new Date()
        });
      }

      // Create a unique name for the recycled item (with timestamp)
      const fileName = path.split('\\').pop() || 'unknown';
      const timestamp = Date.now();
      const recycledPath = `${recycleBinPath}\\${timestamp}_${fileName}`;

      // Move the item to recycle bin
      const originalItem = this.virtualFS.get(path);
      if (originalItem) {
        this.virtualFS.set(recycledPath, {
          ...originalItem,
          // Store original path in a metadata field
          originalPath: path
        });

        // Remove from original location
        this.virtualFS.delete(path);

        // Update persistent storage
        if (originalItem.type === 'file' && originalItem.content) {
          await globalVFS.save(recycledPath, originalItem.content);
        } else {
          await globalVFS.createDirectory(recycledPath);
        }
        await globalVFS.delete(path);

        window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: parentPath } }));
        window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: recycleBinPath } }));
        window.dispatchEvent(new CustomEvent('recycleBinChanged'));

        console.log('Item moved to recycle bin:', path, '→', recycledPath);
        return true;
      }

      return false;
    } catch (e) {
      console.error('Failed to move item to recycle bin:', e);
      return false;
    }
  }

  private isRecycleBinPath(path: string): boolean {
    const normalized = this.normalizePath(path);
    return normalized === 'C:\\$Recycle.Bin' || normalized.startsWith('C:\\$Recycle.Bin\\');
  }

  async permanentlyDeleteItem(path: string): Promise<boolean> {
    try {
      const normalized = this.normalizePath(path);
      const recycleBinPath = 'C:\\$Recycle.Bin';
      const shouldNotifyRecycleBin = this.isRecycleBinPath(normalized);
      const parentPath = this.getParentPath(normalized);
      const toDelete = Array.from(this.virtualFS.keys())
        .filter((entryPath) => entryPath === normalized || entryPath.startsWith(normalized + '\\'))
        .sort((a, b) => b.length - a.length);

      if (toDelete.length === 0) {
        return false;
      }

      for (const entryPath of toDelete) {
        this.virtualFS.delete(entryPath);
        try {
          if (await globalVFS.exists(entryPath)) {
            await globalVFS.delete(entryPath);
          }
        } catch (error) {
          console.warn('permanentlyDeleteItem: failed to delete', entryPath, error);
        }
      }

      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: parentPath } }));

      if (shouldNotifyRecycleBin) {
        window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: recycleBinPath } }));
        window.dispatchEvent(new CustomEvent('recycleBinChanged'));
      }

      return true;
    } catch (e) {
      console.error('Failed to permanently delete item:', e);
      return false;
    }
  }

  async emptyRecycleBin(): Promise<boolean> {
    try {
      const recycleBinPath = 'C:\\$Recycle.Bin';
      const recycledEntries = Array.from(this.virtualFS.keys())
        .filter((entryPath) => entryPath.startsWith(recycleBinPath + '\\'))
        .sort((a, b) => b.length - a.length);

      for (const entryPath of recycledEntries) {
        this.virtualFS.delete(entryPath);
        try {
          if (await globalVFS.exists(entryPath)) {
            await globalVFS.delete(entryPath);
          }
        } catch (error) {
          console.warn('emptyRecycleBin: failed to delete', entryPath, error);
        }
      }

      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: recycleBinPath } }));
      window.dispatchEvent(new CustomEvent('recycleBinChanged'));

      return true;
    } catch (e) {
      console.error('Failed to empty recycle bin:', e);
      return false;
    }
  }

  async restoreFromRecycleBin(path: string): Promise<boolean> {
    try {
      const normalized = this.normalizePath(path);
      const recycledItem = this.virtualFS.get(normalized);
      const originalPath = recycledItem?.originalPath;

      if (!recycledItem || !originalPath || !this.isRecycleBinPath(normalized)) {
        return false;
      }

      const parentPath = this.getParentPath(originalPath);
      const recycleBinPath = 'C:\\$Recycle.Bin';
      if (parentPath && !this.virtualFS.has(parentPath)) {
        await this.createFolder(parentPath);
      }

      let restorePath = originalPath;
      if (this.pathExists(restorePath)) {
        const lastSlash = restorePath.lastIndexOf('\\');
        const baseDir = lastSlash >= 0 ? restorePath.slice(0, lastSlash) : 'C:';
        const fileName = lastSlash >= 0 ? restorePath.slice(lastSlash + 1) : restorePath;
        const dotIndex = recycledItem.type === 'file' ? fileName.lastIndexOf('.') : -1;
        const stem = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
        const suffix = dotIndex > 0 ? fileName.slice(dotIndex) : '';

        let copyIndex = 1;
        do {
          restorePath = `${baseDir}\\${stem} (Restored ${copyIndex})${suffix}`;
          copyIndex += 1;
        } while (this.pathExists(restorePath));
      }

      const restored = await this.renameItem(normalized, restorePath);
      if (restored) {
        window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: recycleBinPath } }));
        window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: this.getParentPath(restorePath) } }));
        window.dispatchEvent(new CustomEvent('recycleBinChanged'));
      }

      return restored;
    } catch (e) {
      console.error('Failed to restore item from recycle bin:', e);
      return false;
    }
  }

  async deleteItem(path: string): Promise<boolean> {
    if (this.isRecycleBinPath(path)) {
      return this.permanentlyDeleteItem(path);
    }

    return this.moveToRecycleBin(path);
  }

  // Recursively rename/move a file or folder, preserving all contents
  async renameItem(oldPath: string, newPath: string): Promise<boolean> {
    try {
      if (oldPath === newPath) return true;

      const source = this.virtualFS.get(oldPath);
      if (!source) {
        console.warn('renameItem: source not found', oldPath);
        return false;
      }

      // Ensure parent of destination exists
      const newParent = this.getParentPath(newPath);
      if (newParent && !this.virtualFS.has(newParent)) {
        await this.createFolder(newParent);
      }

      // Collect all entries to move (source + descendants)
      const toMove: { path: string; item: { type: 'file' | 'folder', content?: string, modified: Date } }[] = [];
      for (const [path, item] of this.virtualFS) {
        if (path === oldPath || path.startsWith(oldPath + '\\')) {
          toMove.push({ path, item });
        }
      }

      // Create destination entries (sorted by length ascending to create parents first)
      toMove.sort((a, b) => a.path.length - b.path.length);
      for (const entry of toMove) {
        const suffix = entry.path === oldPath ? '' : entry.path.substring(oldPath.length);
        const destPath = newPath + suffix;

        if (entry.item.type === 'folder') {
          // Create folder if not exists
          if (!this.virtualFS.has(destPath)) {
            this.virtualFS.set(destPath, {
              type: 'folder',
              modified: new Date()
            });
          }
          // Persist directory
          if (!(await globalVFS.exists(destPath))) {
            await globalVFS.createDirectory(destPath);
          }
        } else {
          // File: copy content
          let content = entry.item.content;
          if (content === undefined) {
            // Load from persistent storage if not in memory
            const loaded = await globalVFS.load(entry.path);
            content = loaded || '';
          }
          this.virtualFS.set(destPath, {
            type: 'file',
            content,
            modified: new Date()
          });
          await globalVFS.save(destPath, content || '');
        }
      }

      // Delete source entries (children first), best-effort
      toMove.sort((a, b) => b.path.length - a.path.length);
      for (const entry of toMove) {
        this.virtualFS.delete(entry.path);
        try {
          if (await globalVFS.exists(entry.path)) {
            await globalVFS.delete(entry.path);
          }
        } catch (e) {
          // Ignore deletion failures for intermediate directories
          console.warn('renameItem: failed to delete', entry.path, e);
        }
      }

      // Emit change for both paths to refresh views
      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: oldPath } }));
      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: newPath } }));

      return true;
    } catch (e) {
      console.error('renameItem failed:', e);
      return false;
    }
  }

  // Get VFS size for debugging
  getVFSSize(): number {
    return this.virtualFS.size;
  }

  // Format bytes to human readable format
  formatBytes(bytes: number): string {
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
    let unitIndex = 0;
    let size = bytes;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  }

  // Get file count in the system
  getFileCount(): { files: number; folders: number } {
    let files = 0;
    let folders = 0;
    
    for (const [_, item] of this.virtualFS) {
      if (item.type === 'file') {
        files++;
      } else {
        folders++;
      }
    }
    
    return { files, folders };
  }
}

// Global helpers for operator API

// Singleton navigator for rename operations; ensures one instance.
const operatorNavigator = new FileSystemNavigator()

// Ensure a directory path exists (idempotent)
export async function ensureDir(path: string): Promise<void> {
  const normalized = path.replace(/\//g, '\\');
  if (!operatorNavigator.pathExists(normalized)) {
    await operatorNavigator.createFolder(normalized);
  }
}

// Ensure a file exists with default content if absent (idempotent)
export async function ensureFile(path: string, defaultContent: string = ''): Promise<void> {
  const normalized = path.replace(/\//g, '\\');
  const exists = operatorNavigator.pathExists(normalized);
  if (!exists) {
    await operatorNavigator.createFile(normalized, defaultContent);
  }
}

// Rename/move path using navigator
export async function renamePath(oldPath: string, newPath: string): Promise<boolean> {
  return operatorNavigator.renameItem(oldPath, newPath)
}